/* ─────────────────────────────────────────────────────────────
   Medicore header — nav dropdowns, mobile drawer, search scope.
   Vanilla, no dependencies. Every interactive control also works
   without JS (nav parents fall back to links, search submits
   normally), so a failure here degrades rather than breaks.
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  /* ── desktop dropdowns ─────────────────────────────────────── */

  function closeAllSubmenus(except) {
    document.querySelectorAll('.mc-nav__item--open').forEach(function (item) {
      if (item === except) return;
      item.classList.remove('mc-nav__item--open');
      var toggle = item.querySelector('[data-mc-submenu-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-mc-submenu-toggle]');

    if (toggle) {
      event.preventDefault();
      var item = toggle.closest('.mc-nav__item');
      var isOpen = item.classList.contains('mc-nav__item--open');
      closeAllSubmenus(item);
      item.classList.toggle('mc-nav__item--open', !isOpen);
      toggle.setAttribute('aria-expanded', String(!isOpen));
      return;
    }

    // a click anywhere outside an open panel closes it
    if (!event.target.closest('.mc-nav__panel')) closeAllSubmenus(null);
  });

  // Hover opens on pointer devices that can hover; touch is unaffected.
  if (window.matchMedia('(hover: hover) and (min-width: 990px)').matches) {
    document.querySelectorAll('.mc-nav__item--has-children').forEach(function (item) {
      var toggle = item.querySelector('[data-mc-submenu-toggle]');
      item.addEventListener('mouseenter', function () {
        closeAllSubmenus(item);
        item.classList.add('mc-nav__item--open');
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
      });
      item.addEventListener('mouseleave', function () {
        item.classList.remove('mc-nav__item--open');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    });
  }

  /* ── mobile drawer ─────────────────────────────────────────── */

  var lastFocused = null;

  function drawer() {
    return document.querySelector('[data-mc-nav]');
  }

  function openNav() {
    var el = drawer();
    if (!el) return;
    lastFocused = document.activeElement;
    el.hidden = false;
    void el.offsetWidth;
    el.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.querySelectorAll('[data-mc-nav-toggle]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'true');
    });
    var close = el.querySelector('[data-mc-nav-close]');
    if (close) close.focus();
  }

  function closeNav() {
    var el = drawer();
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    document.body.style.overflow = '';
    document.querySelectorAll('[data-mc-nav-toggle]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'false');
    });

    var done = function () {
      if (el.classList.contains('is-open')) return;
      el.hidden = true;
      // Only once it is out of sight - resetting mid-slide snaps every
      // panel back where the visitor can still see it happen.
      resetPanels();
    };
    var panel = el.querySelector('.mc-mobile-nav__panel');
    if (panel) {
      panel.addEventListener('transitionend', done, { once: true });
      // Longer than the panel's own transition, so this only ever acts
      // as a backstop when transitionend does not arrive.
      setTimeout(done, 550);
    } else {
      done();
    }

    if (lastFocused && document.contains(lastFocused)) lastFocused.focus();
    lastFocused = null;
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-mc-nav-toggle]')) {
      event.preventDefault();
      var el = drawer();
      if (el && el.classList.contains('is-open')) closeNav();
      else openNav();
      return;
    }

    if (event.target.closest('[data-mc-nav-close]')) {
      event.preventDefault();
      closeNav();
      return;
    }

    var open = event.target.closest('[data-mc-panel-open]');
    if (open) {
      event.preventDefault();
      goToPanel(open.getAttribute('data-mc-panel-open'), 'forward');
      return;
    }

    var back = event.target.closest('[data-mc-panel-back]');
    if (back) {
      event.preventDefault();
      goToPanel(back.getAttribute('data-mc-panel-back'), 'back');
    }
  });

  /* ── drawer panels ──────────────────────────────────────────
     Panels are laid on top of each other and slide sideways rather
     than expanding in place. An accordion three levels deep is taller
     than the phone, and every row already read has moved by the time
     the third level opens; here nothing that has been seen moves.

     A panel that is left behind is parked, not unmounted, so it keeps
     its scroll position and going back lands where you left.
     ─────────────────────────────────────────────────────────── */

  var REDUCED = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  function stack() {
    return document.querySelector('[data-mc-stack]');
  }

  function panelById(id) {
    var el = stack();
    return el ? el.querySelector('[data-mc-panel="' + id + '"]') : null;
  }

  function goToPanel(id, direction) {
    var el = stack();
    var next = panelById(id);
    if (!el || !next) return;

    var current = el.querySelector('.mc-mnav-panel.is-active');
    if (current === next) return;

    // Cleared before the transform so the browser has a laid-out box to
    // animate from; a hidden element has no geometry to move.
    next.hidden = false;
    void next.offsetWidth;

    if (current) {
      current.classList.remove('is-active');
      if (direction === 'forward') {
        current.classList.add('is-parked');
      } else {
        /* Going back, the panel being left has to travel out to the
           right. Parked would send it the wrong way. */
        current.classList.remove('is-parked');
        hideAfterTransition(current);
      }
    }

    next.classList.remove('is-parked');
    next.classList.add('is-active');

    // A panel arriving from a previous visit would otherwise open
    // wherever it was last scrolled to.
    next.scrollTop = 0;

    var focusTarget = next.querySelector('[data-mc-panel-back], .mc-mnav__row');
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  /* Kept in the DOM until it has finished moving, then taken out of the
     tab order - a panel off to the right is still focusable otherwise. */
  function hideAfterTransition(panel) {
    if (REDUCED.matches) {
      panel.hidden = true;
      return;
    }

    var done = function () {
      panel.removeEventListener('transitionend', done);
      clearTimeout(panel._mcTimer);
      if (!panel.classList.contains('is-active')) panel.hidden = true;
    };

    panel.addEventListener('transitionend', done);
    // transitionend never arrives in a backgrounded tab, and a panel
    // stuck visible would overlap the one behind it.
    panel._mcTimer = setTimeout(done, 450);
  }

  /* The drawer reopens at the root. Resuming four levels deep with no
     memory of how you got there is disorienting, and the back rows
     would be describing a journey the visitor did not take. */
  function resetPanels() {
    var el = stack();
    if (!el) return;

    Array.prototype.forEach.call(el.querySelectorAll('.mc-mnav-panel'), function (panel) {
      var isRoot = panel.getAttribute('data-mc-panel') === 'root';
      panel.classList.toggle('is-active', isRoot);
      panel.classList.remove('is-parked');
      panel.hidden = !isRoot;
      panel.scrollTop = 0;
    });
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    /* One level at a time. Escape from three levels deep should undo
       the last step, not throw the whole drawer away. */
    var el = drawer();
    if (el && el.classList.contains('is-open')) {
      var active = stack() && stack().querySelector('.mc-mnav-panel.is-active');
      var back = active && active.querySelector('[data-mc-panel-back]');
      if (back) {
        goToPanel(back.getAttribute('data-mc-panel-back'), 'back');
        return;
      }
    }

    closeNav();
    closeAllSubmenus(null);
  });

  /* ── search category scope ─────────────────────────────────── */

  /*
   * Shopify search has no collection filter, so a chosen category is
   * appended to the query as a product_type field term. Note this
   * relies on product_type being populated - it is currently empty
   * across the catalogue, so scoping returns nothing until that data
   * lands. "All categories" (no scope) is unaffected.
   */
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-mc-search-form]');
    if (!form) return;

    var scope = form.querySelector('[data-mc-search-scope]');
    var input = form.querySelector('input[name="q"]');
    if (!scope || !input || !scope.value) return;

    var term = 'product_type:' + JSON.stringify(scope.value);
    if (input.value.indexOf('product_type:') === -1) {
      input.value = (input.value.trim() + ' ' + term).trim();
    }
  });
})();
